// Library import
import { useEffect, useState, useRef } from 'react';
import { Alert, Modal, Form } from 'react-bootstrap';
import Proptypes from 'prop-types';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

// File Import
import CrossIcon from '~/images/icon/cross-gray.png';
import LeftArrow from '~/images/icon/left-arrow.png';
import { getTimes, getDate } from '@/constant';
import { formatDate } from '@/config';

// Components Import
import { PostApi } from '@/http';

export const ScheduleModal = ({ show, handleClose, postStatus, payload, mapTitle }) => {
  const url = new URL(window.location.href);
  const timeLists = useRef(null);
  const defaultTime = 14;
  const [scheduleDate, setScheduleDate] = useState(getDate(defaultTime));
  const [scheduleTime, setScheduleTime] = useState(`${String(defaultTime).padStart(2, '0')}:00`);
  const [scheduleError, setServerError] = useState('');
  const [disableSubmitBtn, setDisableSubmitBtn] = useState(false);
  const [showTimeList, setShowTimeList] = useState(false);
  const [isScheduled, setIsScheduled] = useState(false);
  const expiredTimeLists = useRef(null);
  const [expiredDate, setExpiredDate] = useState(new Date());
  const [expiredTime, setExpiredTime] = useState('');
  const [expiredError, setExpiredServerError] = useState('');
  const [showExpiredTimeList, setShowExpiredTimeList] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);

  useEffect(() => {
    if (payload?.status !== 'draft' && payload?.scheduled_at) {
      setIsScheduled(true);
    }

    if (payload?.status !== 'draft' && payload?.expired_at) {
      setExpiredDate(new Date(payload?.expired_at.substr(0, 10)));
      setExpiredTime(payload?.expired_at.substr(11));
      setIsDisabled(false);
    }

    return () => {
      setServerError('');
      setServerError('');
      setExpiredServerError('');
    };
  }, [show]);

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (showTimeList) {
      setScrollPosition();
    }
  }, [showTimeList]);

  const handleClickOutside = (event) => {
    if (timeLists.current && (!timeLists.current.contains(event.target) || event.target.tagName == 'LI')) {
      setShowTimeList(false);
    }
    if (expiredTimeLists.current && (!expiredTimeLists.current.contains(event.target) || event.target.tagName == 'LI')) {
      setShowExpiredTimeList(false);
    }
  };

  const handleCheckboxChange = (event) => {
    setIsDisabled(event.target.checked);
  };

  const handleSubmitPost = async () => {
    const scheduled_at = `${formatDate(scheduleDate, 'YYYY-MM-DD')} ${scheduleTime}`;

    const expired_at = !isDisabled && postStatus === 'scheduled' ? `${formatDate(expiredDate, 'YYYY-MM-DD')} ${expiredTime}` : '';

    let formData = new FormData();

    formData.append('title', payload?.title);
    formData.append('image', payload?.image);
    formData.append('url', payload?.url);
    formData.append('tags', JSON.stringify(payload.tags));
    formData.append('type', payload?.type);
    formData.append('status', postStatus);
    formData.append('notification_flg', payload?.notification_flg);
    formData.append('published_at', payload?.published_at);
    formData.append('expired_at', expired_at);
    formData.append('user_name', payload?.user_name);
    formData.append('calendar_info', '');
    // セミナーとして登録する check
    if (payload?.ci_seminar_flg === 1) {
      const startDate = `${formatDate(payload?.ci_start_date, 'YYYY-MM-DD')} ${payload?.ci_start_time}:${payload?.ci_start_minute}`;
      const endDate = `${formatDate(payload?.ci_end_date, 'YYYY-MM-DD')} ${payload?.ci_end_time}:${payload?.ci_end_minute}`;
      const seminar_items = [];
      payload.ci_seminar_items.forEach((element) => {
        const startDateData = `${formatDate(element.ci_start_date, 'YYYY-MM-DD')} ${element.ci_start_time}:${element.ci_start_minute}`;
        const endDateData = `${formatDate(element.ci_end_date, 'YYYY-MM-DD')} ${element.ci_end_time}:${element.ci_end_minute}`;
        seminar_items.push({
          address: element.ci_address && element.ci_address !== '' ? element.ci_address : '',
          description: element.ci_description && element.ci_description !== '' ? element.ci_description : '',
          end_date: element.ci_end_date && element?.ci_end_time && element?.ci_end_minute ? endDateData : '',
          start_date: element.ci_start_date && element?.ci_start_time && element?.ci_start_minute ? startDateData : '',
          title: element.ci_title && element.ci_title !== '' ? element.ci_title : '',
          url: element.ci_url && element.ci_url !== '' ? element.ci_url : ''
        });
      });
      const obj = {
        address: seminar_items[0].address,
        description: seminar_items[0].description,
        end_date: seminar_items[0].end_date,
        start_date: seminar_items[0].start_date,
        seminar_items: seminar_items,
        title: seminar_items[0].title,
        url: seminar_items[0].url
      };
      formData.append('calendar_info', JSON.stringify(obj));
    }
    if (payload?.id) formData.append('_method', 'PUT');

    if (postStatus === 'scheduled') {
      formData.append('scheduled_at', scheduled_at);
    }

    if (payload?.type === 'case_study') {
      payload.items.forEach((item, index) => {
        formData.append(`items[${index}][sub_items][title]`, item.title ? item.title : '');
        item.sub_items.forEach((sub, idx) => {
          formData.append(`items[${index}][sub_items][${idx}][title]`, sub.title ? sub.title : '');
          formData.append(`items[${index}][sub_items][${idx}][description]`, sub.description ? sub.description : '');
          if (sub.image !== undefined) {
            formData.append(`items[${index}][sub_items][${idx}][image_url]`, sub.image ? sub.image : '');
          } else {
            formData.append(`items[${index}][sub_items][${idx}][image_url]`, sub.image_url ? sub.image_url : '');
          }
        });
      });
    } else {
      if (payload?.map_flg === 0) {
        const newItems = [];
        newItems.push(payload.items);

        payload.items = payload.items.filter(function (item) {
          return item.title !== mapTitle;
        });
      }
      payload.items.forEach((item, index) => {
        formData.append(`items[${index}][title]`, item.title ? item.title : '');
        formData.append(`items[${index}][title_free]`, item.title_free ? item.title_free : '');
        formData.append(`items[${index}][description]`, item.description ? item.description : '');
        if (item.map_items && item.map_items.length > 0 && payload?.map_flg === 1) {
          item.map_items.forEach((map, idx) => {
            formData.append(`items[${index}][map_items][${idx}][address]`, map.address ? map.address : '');
            formData.append(`items[${index}][map_items][${idx}][place_id]`, map.place_id ? map.place_id : '');
            formData.append(`items[${index}][map_items][${idx}][place_name]`, map.place_name ? map.place_name : '');
            formData.append(`items[${index}][map_items][${idx}][latitude]`, map.latitude ? map.latitude : '');
            formData.append(`items[${index}][map_items][${idx}][longitude]`, map.longitude ? map.longitude : '');
          });
        }
      });
    }

    if (postStatus === 'scheduled' && scheduleTime === '') {
      setServerError('掲載開始の日時を選択してください');
      return;
    } else {
      setServerError('');
    }

    if (!isDisabled && expiredTime === '') {
      setExpiredServerError('掲載終了の日時を選択してください');
      return;
    } else if (postStatus === 'scheduled' && !isDisabled && new Date(scheduled_at).getTime() >= new Date(expired_at).getTime()) {
      setExpiredServerError('掲載終了日には開始日以降の日付を指定して下さい');
      return;
    } else {
      setExpiredServerError('');
    }

    try {
      setDisableSubmitBtn(true);
      sessionStorage.clear();
      const response = payload?.id ? await PostApi.updatePost(payload?.id, formData) : await PostApi.createPost(formData);

      if (response.status === 200) {
        setDisableSubmitBtn(true);
        sessionStorage.setItem('reloadType', `${payload?.type}`);
        const searchStr = url.searchParams.get('search') ? 'search=' + url.searchParams.get('search') + '&&' : '';
        const sizeStr = url.searchParams.get('size') ? '&&size=' + url.searchParams.get('size') : '';
        const urlRedirect = `/admin/posts?${searchStr}type=${payload?.type}${sizeStr}`;
        window.open(urlRedirect, '_self');
        // window.open(`/admin/posts?type=${payload?.type}`, '_self');
      }
    } catch (error) {
      setDisableSubmitBtn(false);
      if (error.response.data?.errors?.scheduled_at) {
        setServerError(error.response.data?.message);
      } else {
        setServerError('Server error occured !');
      }
    }
  };

  const setScrollPosition = () => {
    const element = document.getElementById('time11');
    if (element) {
      element.scrollIntoView();
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered animation={true} className='modal-wrapper' backdrop='static'>
      <div className='p-5'>
        <div className='modal-common-content schedule-form'>
          {postStatus === 'scheduled' && (
            <div>
              <Form.Label>掲載予定</Form.Label>
              <div className='d-flex align-items-center'>
                <Form.Group className='mb-3 w-100' controlId='postTitle'>
                  <div className='position-relative'>
                    <DatePicker
                      selected={scheduleDate}
                      placeholderText='Select Date'
                      onChange={(date) => {
                        setScheduleDate(date);
                        if (date) setIsScheduled(false);
                      }}
                      minDate={new Date()}
                      dateFormat='yyyy-MM-dd'
                      className='form-control'
                    />
                    {isScheduled && <div className='schedule-date'>{formatDate(scheduleDate, 'YYYY-MM-DD')}</div>}
                  </div>
                </Form.Group>

                <Form.Group ref={timeLists} className='mb-3 ms-2 w-100' controlId='scheduleTime'>
                  <div className='position-relative'>
                    <Form.Control
                      name='schedule_time'
                      onFocus={(event) => {
                        event.target.select();
                        setShowTimeList(true);
                      }}
                      onChange={(event) => {
                        event.target.value ? setServerError('') : setServerError('掲載開始の日時を選択してください');
                        setScheduleTime(event.target.value);
                      }}
                      type='text'
                      placeholder='時間を選択してください...'
                      value={scheduleTime}
                      maxLength={5}
                    />

                    {/* Arrow icon */}
                    <div className='select-carret'>
                      <img src={LeftArrow} alt='Left arrow' />
                    </div>

                    {/* Time list shown here */}
                    {showTimeList && (
                      <ul className='time-list'>
                        {getTimes()?.map((option, idx) => (
                          <li key={idx} className='time' onClick={() => setScheduleTime(option)} id={`time${idx}`}>
                            {option}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </Form.Group>
              </div>
            </div>
          )}

          {scheduleTime === '' ||
            (scheduleDate === '' && (
              <Alert variant='danger' className='py-1 my-3 m-auto text-center w-50'>
                Please select schedule date and time before submit
              </Alert>
            ))}

          {scheduleError && <div className='error-message'>{scheduleError}</div>}

          {postStatus === 'scheduled' && (
            <div>
              <Form.Label>掲載終了</Form.Label>
              <div className='d-flex align-items-center'>
                <Form.Group className='mb-3 w-100' controlId='expiredDate'>
                  <div className='position-relative'>
                    <DatePicker
                      selected={expiredDate}
                      placeholderText='Select Date'
                      onChange={(date) => {
                        if (date) setExpiredDate(date);
                        if (!date) setExpiredDate(new Date());
                      }}
                      minDate={new Date()}
                      maxDate={new Date('9999-12-31')}
                      dateFormat='yyyy-MM-dd'
                      className='form-control'
                      disabled={isDisabled}
                    />
                  </div>
                </Form.Group>

                <Form.Group ref={expiredTimeLists} className='mb-3 ms-2 w-100' controlId='expiredTime'>
                  <div className='position-relative'>
                    <Form.Control
                      name='ended_time'
                      onFocus={(event) => {
                        event.target.select();
                        setShowExpiredTimeList(true);
                      }}
                      onChange={(event) => {
                        event.target.value ? setExpiredServerError('') : setExpiredServerError('掲載終了の日時を選択してください');
                        setExpiredTime(event.target.value);
                      }}
                      type='text'
                      placeholder='時間を選択してください...'
                      value={expiredTime}
                      maxLength={5}
                      disabled={isDisabled}
                    />

                    {/* Arrow icon */}
                    <div className='select-carret'>
                      <img src={LeftArrow} alt='Left arrow' />
                    </div>

                    {/* Time list shown here */}
                    {showExpiredTimeList && (
                      <ul className='time-list'>
                        {getTimes()?.map((option, idx) => (
                          <li key={idx} className='time' onClick={() => setExpiredTime(option)}>
                            {option}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </Form.Group>
              </div>
            </div>
          )}

          {expiredError && <div className='error-message'>{expiredError}</div>}

          {postStatus === 'scheduled' && (
            <div className='mb-3'>
              <input
                type='checkbox'
                id='disabled_flg'
                name='disabled_flg'
                value={isDisabled}
                checked={isDisabled}
                onChange={handleCheckboxChange}
              />
              <label htmlFor='disabled_flg'>掲載終了日時を設定しない</label>
            </div>
          )}

          <div className='action-btn mt-2 d-flex justify-content-center align-items-center'>
            <button className='button outline' onClick={handleClose}>
              キャンセル
            </button>
            <button className='button primary ms-2' type='submit' disabled={disableSubmitBtn} onClick={handleSubmitPost}>
              確定
            </button>
          </div>
        </div>

        {/* Server error */}
        {/* {serverError && (
          <Alert variant='danger' className='py-1 my-3 m-auto text-center w-75'>
            {serverError}
          </Alert>
        )} */}

        <div className='close-modal' onClick={handleClose}>
          <img src={CrossIcon} alt='Cross icon' />
        </div>
      </div>
    </Modal>
  );
};

ScheduleModal.prototype = {
  show: Proptypes.bool,
  handleClose: Proptypes.func,
  postStatus: Proptypes.string,
  payload: Proptypes.object,
  mapTitle: Proptypes.string
};
